import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import useLiveMonitoring from '../hooks/useWebSocket';
import { Radio, Users, Clock, MessageSquare, ArrowLeft, Wifi, WifiOff, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LiveDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get user info
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // Live monitoring with REST polling
  const { isConnected, interviews, reconnect, error } = useLiveMonitoring(
    user?.user_id,
    BACKEND_URL
  );

  // Show toast when connection status changes
  useEffect(() => {
    if (isConnected) {
      toast.success('Connecté au monitoring en direct');
    }
  }, [isConnected]);

  // Select first interview by default
  useEffect(() => {
    if (interviews.length > 0 && !selectedInterview) {
      setSelectedInterview(interviews[0]);
    }
    // Update selected interview data
    if (selectedInterview) {
      const updated = interviews.find(i => i.session_id === selectedInterview.session_id);
      if (updated) {
        setSelectedInterview(updated);
      } else {
        // Interview ended, select another
        setSelectedInterview(interviews[0] || null);
      }
    }
  }, [interviews, selectedInterview]);

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas dark:bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 sm:gap-3">
                <Radio className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 animate-pulse" />
                Monitoring en Direct
              </h1>
              <p className="text-zinc-400 mt-1 text-sm sm:text-base">
                Suivez les entretiens en cours en temps réel
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={reconnect}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Reconnecter"
              data-testid="reconnect-btn"
            >
              <RefreshCw className="w-5 h-5 text-zinc-400" />
            </button>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              isConnected ? 'bg-lime-500/20 text-lime-400' : 'bg-red-500/20 text-red-400'
            }`}>
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4" />
                  <span className="font-medium">Connecté</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="font-medium">Déconnecté</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        {interviews.length === 0 ? (
          <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-12 text-center">
            <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Aucun entretien en cours
            </h2>
            <p className="text-zinc-400 max-w-md mx-auto">
              Les entretiens en cours apparaîtront ici en temps réel. 
              Vous verrez les transcriptions au fur et à mesure.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Interview List */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-4">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-lime-400" />
                  Entretiens actifs ({interviews.length})
                </h2>
                <div className="space-y-2">
                  {interviews.map((interview) => (
                    <button
                      key={interview.session_id}
                      onClick={() => setSelectedInterview(interview)}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        selectedInterview?.session_id === interview.session_id
                          ? 'bg-lime-500/20 border border-lime-500/50'
                          : 'bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600'
                      }`}
                      data-testid={`interview-item-${interview.session_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-white">
                          {interview.candidate_name}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-red-400">
                          <Radio className="w-3 h-3 animate-pulse" />
                          En direct
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400 truncate">
                        {interview.job_title}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          Q{interview.current_question_index || 0}/{interview.total_questions || '?'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(interview.started_at)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Transcript View */}
            <div className="lg:col-span-2">
              {selectedInterview ? (
                <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-white">
                        {selectedInterview.candidate_name}
                      </h2>
                      <p className="text-zinc-400">{selectedInterview.job_title}</p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-full">
                      <Radio className="w-4 h-4 text-red-400 animate-pulse" />
                      <span className="text-sm text-red-400 font-medium">En direct</span>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-zinc-400">Progression</span>
                      <span className="text-white font-medium">
                        Question {selectedInterview.current_question_index || 0} / {selectedInterview.total_questions || '?'}
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-lime-500 to-emerald-500 transition-all duration-500"
                        style={{ 
                          width: `${((selectedInterview.current_question_index || 0) / (selectedInterview.total_questions || 1)) * 100}%` 
                        }}
                      />
                    </div>
                  </div>

                  {/* Transcript */}
                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                    <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                      Transcription en temps réel
                    </h3>
                    
                    {selectedInterview.transcript && selectedInterview.transcript.length > 0 ? (
                      selectedInterview.transcript.map((entry, index) => (
                        <div key={index} className="space-y-2">
                          <div className="bg-zinc-800/50 rounded-lg p-3">
                            <p className="text-xs text-lime-400 font-medium mb-1">Question</p>
                            <p className="text-zinc-300">{entry.q}</p>
                          </div>
                          <div className="bg-blue-500/10 rounded-lg p-3 ml-4">
                            <p className="text-xs text-blue-400 font-medium mb-1">Réponse</p>
                            <p className="text-zinc-300">{entry.r}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                        <p className="text-zinc-500">
                          En attente des premières réponses...
                        </p>
                      </div>
                    )}

                    {/* Last response indicator */}
                    {selectedInterview.last_response && (
                      <div className="border-l-2 border-lime-500 pl-4 py-2">
                        <p className="text-xs text-lime-400 mb-1">Dernière réponse</p>
                        <p className="text-zinc-300 text-sm italic">
                          "{selectedInterview.last_response}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">
                    Sélectionnez un entretien pour voir la transcription
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Helper function to format time
const formatTime = (isoString) => {
  if (!isoString) return '--:--';
  const date = new Date(isoString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export default LiveDashboard;
